import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Award } from 'lucide-react';
import Header from '../components/Header';
import { quizQuestions } from '../data/quizData';
import { tracker } from '../lib/tracker';
import { SkeletonLoader, ErrorDisplay, EnhancedProgressBar, useToast } from '../components/common/FeedbackSystem';
import useConnectivity from '../hooks/useConnectivity';

interface QuizResponse {
  question_id: string;
  reponse: string;
  score_partiel: number;
}

const QuizPage: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<QuizResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const { token: pathToken } = useParams<{ token?: string }>();
  const { showError, showSuccess, showInfo, ToastContainer } = useToast();
  const { isOffline, queueAction } = useConnectivity();

  // Récupérer le token depuis les paramètres de requête (pour navigation privée)
  const getEffectiveToken = (): string | undefined => {
    // Vérifier d'abord les paramètres de requête
    const urlParams = new URLSearchParams(window.location.search);
    const queryToken = urlParams.get('token');
    
    // Utiliser le token des paramètres ou celui du chemin
    return queryToken || pathToken;
  };

  // Charger l'état initial du quiz
  useEffect(() => {
    const initializeQuiz = async () => {
      const effectiveToken = getEffectiveToken();
      
      
      if (!effectiveToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Marquer la page comme vue
        await tracker.trackPageView(effectiveToken);
        
        // Initialiser le quiz
        await tracker.trackQuizStart(effectiveToken);

        // Récupérer l'état actuel du quiz
        const quizState = await tracker.getQuizState(effectiveToken);
        
        // Si le quiz est déjà terminé, rediriger vers la page de score
        if (quizState.isComplete) {
          navigate(`/monscore/${effectiveToken}`);
          return;
        }
        
        // Charger les réponses existantes
        if (quizState.responses.length > 0) {
          setResponses(quizState.responses);
          // Positionner à la question suivante
          setCurrentQuestion(Math.min(quizState.responses.length, quizQuestions.length - 1));
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing quiz:', err);

        setError('Une erreur est survenue lors du chargement du quiz. Veuillez réessayer.');
        setIsLoading(false);
      }
    };

    initializeQuiz();
  }, [navigate, pathToken]);

  const calculatePartialScore = (questionId: number, answer: string): number => {
    // This is a simple scoring algorithm - in production this would be more sophisticated
    const scores = {
      1: { 'Moins de 10 %': 5, 'Entre 10 % et 30 %': 15, 'Entre 30 % et 60 %': 25, 'Plus de 60 %': 30 },
      2: { 'Générer suffisamment de leads qualifiés': 25, 'Convertir les visiteurs en clients': 30, 'Mieux mesurer mes performances': 20, 'Je ne sais pas vraiment par où commencer': 5 },
      3: { 'Oui, activement': 30, 'J\'ai commencé à tester': 20, 'J\'envisage de le faire prochainement': 10, 'Pas du tout pour l\'instant': 0 },
      4: { 'Oui, nous utilisons un CRM et de l\'automation': 30, 'Oui, mais partiellement': 20, 'Nous utilisons encore peu d\'outils': 10, 'Pas du tout': 0 },
      5: { 'Oui, avec des outils de tracking performants': 30, 'Partiellement, via quelques KPIs': 20, 'Très peu, c\'est flou': 10, 'Pas du tout': 0 },
      6: { 'SEO / contenu organique': 25, 'Publicité en ligne (Google Ads, Meta…)': 25, 'Emailing ou CRM': 20, 'Aucun canal ne se démarque vraiment': 5 },
      7: { 'Moins de 5 000 CHF': 5, 'Entre 5 000 et 20 000 CHF': 15, 'Entre 20 000 et 50 000 CHF': 25, 'Plus de 50 000 CHF': 30 }
    };
    
    return scores[questionId as keyof typeof scores][answer as keyof typeof scores[keyof typeof scores]] || 0;
  };

  const calculateTotalScore = (allResponses: QuizResponse[]): number => {
    const totalPossibleScore = 30 * quizQuestions.length; // 30 points max per question
    const actualScore = allResponses.reduce((sum, response) => sum + response.score_partiel, 0);
    return Math.round((actualScore / totalPossibleScore) * 100);
  };

  const handleAnswer = async (answer: string) => {
    if (isSaving) return; // Éviter les soumissions multiples
    
    try {
      setIsSaving(true);
      
      const newResponse = {
        question_id: (currentQuestion + 1).toString(),
        reponse: answer,
        score_partiel: calculatePartialScore(currentQuestion + 1, answer)
      };

      const updatedResponses = [...responses, newResponse];
      setResponses(updatedResponses);

      // Récupérer le token effectif
      const effectiveToken = getEffectiveToken();
      
      // Gestion hors ligne
      if (isOffline && effectiveToken) {
        // Mettre en queue pour synchronisation ultérieure
        queueAction('quiz_response', {
          token: effectiveToken,
          response: newResponse,
          questionIndex: updatedResponses.length
        }, { priority: 'high' });
        
        showSuccess('Réponse enregistrée', 'Votre réponse sera synchronisée dès la reconnexion');
      } else if (effectiveToken) {
        // Sauvegarder la réponse et mettre à jour l'état du quiz
        try {
          await tracker.saveQuizResponse(effectiveToken, newResponse, updatedResponses.length);
          showSuccess('Réponse enregistrée', 'Votre progression a été sauvegardée');
        } catch (err) {
          // En cas d'erreur, mettre en queue
          queueAction('quiz_response', {
            token: effectiveToken,
            response: newResponse,
            questionIndex: updatedResponses.length
          }, { priority: 'high' });
          
          showError('Erreur de sauvegarde', 'Votre réponse sera synchronisée automatiquement');
        }
      }

      if (currentQuestion < quizQuestions.length - 1) {
        // Passer à la question suivante
        setCurrentQuestion(prev => prev + 1);
      } else {
        // Terminer le quiz
        if (effectiveToken) {
          try {
            const totalScore = calculateTotalScore(updatedResponses);

            if (isOffline) {
              // En mode hors ligne, naviguer directement
              showInfo('Quiz terminé', 'Votre score sera calculé à la reconnexion');
              navigate(`/monscore/${effectiveToken}`);
            } else {
              // Attendre que la mise à jour soit terminée avant de naviguer
              const result = await tracker.trackQuizComplete(effectiveToken, totalScore);
              
              // Attendre un peu avant de naviguer pour s'assurer que la BD est mise à jour
              setTimeout(() => {
                navigate(`/monscore/${effectiveToken}`);
              }, 500);
            }
          } catch (error) {
            console.error('Erreur lors de la mise à jour du score:', error);
            navigate('/monscore');
          }
        } else {
          navigate('/monscore');
        }
      }
    } catch (err) {
      console.error('Error saving answer:', err);
      showError('Erreur', 'Une erreur est survenue lors de l\'enregistrement de votre réponse');
      setError('Une erreur est survenue lors de l\'enregistrement de votre réponse. Veuillez réessayer.');
    } finally {
      setIsSaving(false);
    }
  };

  // Gérer les cas de chargement et d'erreur
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-3xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              Évaluation digitale
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Chargement de votre diagnostic...</h1>
          </div>
          <SkeletonLoader variant="quiz" />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Header />
        <main className="container mx-auto px-4 py-8 max-w-3xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              Évaluation digitale
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Une erreur est survenue</h1>
          </div>
          <ErrorDisplay
            message={error}
            onRetry={() => window.location.reload()}
            context="Chargement du quiz"
          />
        </main>
        <ToastContainer />
      </div>
    );
  }

  const question = quizQuestions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header />
      
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-orange-400/3 rounded-full blur-3xl"></div>
      </div>
      
      <main className="container mx-auto px-4 py-8 max-w-3xl relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
            <Award className="w-4 h-4 mr-2" />
            Évaluation digitale
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Diagnostic de votre <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">performance digitale</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Question {currentQuestion + 1} sur {quizQuestions.length} • Quelques minutes seulement
          </p>
        </motion.div>
        
        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <EnhancedProgressBar 
            current={currentQuestion + 1} 
            total={quizQuestions.length}
            variant="steps"
            showPercentage={true}
            color="orange"
            animated={true}
            className="mb-12"
          />
        </motion.div>

        {/* Question Card */}
        <motion.div 
          className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {/* Question Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-8 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-white leading-relaxed">
              {question.question}
            </h2>
          </div>

          {/* Options */}
          <div className="p-8">
            <div className="space-y-4">
              {question.options.map((option, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  disabled={isSaving}
                  className={`group w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 ${
                    isSaving 
                      ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50' 
                      : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50 hover:shadow-lg transform hover:-translate-y-1'
                  }`}
                  whileHover={!isSaving ? { scale: 1.02 } : {}}
                  whileTap={!isSaving ? { scale: 0.98 } : {}}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + (index * 0.1) }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-800 font-medium text-lg leading-relaxed pr-4">
                      {option}
                    </span>
                    <ArrowRight className={`w-5 h-5 transition-all duration-300 ${
                      isSaving ? 'text-gray-400' : 'text-gray-400 group-hover:text-orange-500 group-hover:translate-x-1'
                    }`} />
                  </div>
                </motion.button>
              ))}
            </div>
            
            {/* Loading State */}
            {isSaving && (
              <motion.div 
                className="flex justify-center items-center space-x-3 mt-8 p-4 bg-orange-50 rounded-2xl border border-orange-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-orange-700 font-medium">Enregistrement de votre réponse...</span>
              </motion.div>
            )}
          </div>
        </motion.div>
        
        {/* Progress Indicator */}
        <motion.div 
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="inline-flex items-center space-x-2 text-sm text-gray-600">
            <CheckCircle className="w-4 h-4 text-orange-500" />
            <span>Vos réponses sont automatiquement sauvegardées</span>
          </div>
        </motion.div>
      </main>
      <ToastContainer />
    </div>
  );
};

export default QuizPage;
